'use client';

import { MediaAsset, MediaSource } from 'models/contentful/media/types';
import { FC, useEffect, useState } from 'react';
import styled from 'styled-components';

interface VideoProps extends MediaSource {
  showVideoControls?: boolean;
  initialSource?: MediaAsset | null;
}

export type BandwidthSupportingNavigator = Partial<
  Navigator & {
    connection?: { effectiveType?: string };
  }
>;

type PickVideoSourceByBandwidth = (args: {
  highResolutionVideo?: MediaAsset | null;
  lowResolutionVideo?: MediaAsset | null;
  setSource: (source: MediaAsset) => void;
  navigator?: Navigator;
}) => () => void;

export const pickVideoSourceByBandwidth: PickVideoSourceByBandwidth =
  ({ highResolutionVideo, lowResolutionVideo, setSource, navigator: n }) =>
  () => {
    const nav = n as BandwidthSupportingNavigator | undefined;

    if (!highResolutionVideo && !lowResolutionVideo) {
      return;
    }

    if (highResolutionVideo && !lowResolutionVideo) {
      setSource(highResolutionVideo);
      return;
    }

    if (lowResolutionVideo && !highResolutionVideo) {
      setSource(lowResolutionVideo);
      return;
    }

    if (
      !nav?.connection?.effectiveType ||
      nav?.connection?.effectiveType === '4g'
    ) {
      setSource(highResolutionVideo as MediaAsset);
      return;
    }

    setSource(lowResolutionVideo as MediaAsset);
  };

const StyledVideo = styled.video`
  object-fit: cover;
  height: 100%;
  width: 100%;
`;

const Video: FC<VideoProps> = ({
  image: poster,
  showVideoControls,
  highResolutionVideo,
  lowResolutionVideo,
  initialSource = null,
}) => {
  const [source, setSource] = useState<MediaAsset | null>(initialSource);

  useEffect(
    pickVideoSourceByBandwidth({
      highResolutionVideo,
      lowResolutionVideo,
      navigator,
      setSource,
    }),
    [highResolutionVideo?.url, lowResolutionVideo?.url]
  );

  if (!source?.url) {
    return null;
  }

  return (
    <StyledVideo
      loop={true}
      autoPlay={true}
      muted={process.env.NODE_ENV !== 'test'}
      playsInline={true}
      controls={showVideoControls}
      poster={poster.url + '?auto=compress,format'}
      width="100%"
      height="auto"
    >
      <source type="video/mp4" src={source.url} />
    </StyledVideo>
  );
};

export default Video;
